import * as React from 'react';
import type { ProgrammaticRegionData, ProgrammaticMapMode } from '../../types/programmatic';
import type { StateData, MapColorScale } from '../../types/geographic';
import GoogleMapDataLayer from '../geographic/GoogleMapDataLayer';

interface ProgrammaticMapViewProps {
  data: ProgrammaticRegionData[];
  mapMode: ProgrammaticMapMode;
  isLoading: boolean;
  onStateClick?: (stateCode: string) => void;
}

const getColorScale = (mapMode: ProgrammaticMapMode): MapColorScale => {
  switch (mapMode) {
    case 'native_strength':
      return {
        min: 0, max: 100,
        colors: ['#dc3545', '#ffc107', '#198754'],
        breaks: [0, 33, 66, 100],
      };
    case 'paid_reliance':
      return {
        min: 0, max: 100,
        colors: ['#198754', '#ffc107', '#dc3545'],
        breaks: [0, 33, 66, 100],
      };
    case 'pressure_score':
      return {
        min: 0, max: 100,
        colors: ['#198754', '#ffc107', '#dc3545'],
        breaks: [0, 33, 66, 100],
      };
  }
};

const getValue = (region: ProgrammaticRegionData, mapMode: ProgrammaticMapMode): number => {
  switch (mapMode) {
    case 'native_strength':
      return region.native_pct;
    case 'paid_reliance':
      return region.total_apps > 0 ? 100 - region.native_pct : 0;
    case 'pressure_score':
      return Math.min(region.pressure_score * 25, 100); // normalize to 0-100
  }
};

const transformToStateData = (
  data: ProgrammaticRegionData[],
  mapMode: ProgrammaticMapMode
): StateData[] => {
  return data
    .filter((r) => r.state)
    .map((r) => ({
      state_code: r.state!,
      state_name: r.state!,
      name: r.state!,
      user_count: getValue(r, mapMode),
      client_count: r.js_apps,
      professional_count: r.native_apps,
      revenue: r.total_jobs,
    }));
};

const ProgrammaticMapView: React.FC<ProgrammaticMapViewProps> = ({
  data,
  mapMode,
  isLoading,
  onStateClick,
}) => {
  const stateData = React.useMemo(
    () => transformToStateData(data || [], mapMode),
    [data, mapMode]
  );

  const colorScale = React.useMemo(() => getColorScale(mapMode), [mapMode]);

  const handleStateClick = React.useCallback(
    (state: StateData) => {
      if (onStateClick && state.state_code) {
        onStateClick(state.state_code);
      }
    },
    [onStateClick]
  );

  if (isLoading) {
    return (
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading map...</span>
          </div>
          <p className="text-muted mt-2 mb-0">Loading map data...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.length) {
    return (
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body text-center py-5">
          <i className="fas fa-map text-muted mb-2" style={{ fontSize: '2rem' }} />
          <p className="text-muted mb-0">No geographic data available for the selected filters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-header bg-white border-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="fas fa-map-marked-alt me-2"></i>
            Supply Distribution Map
          </h6>
          <div className="d-flex align-items-center gap-3">
            <small className="text-muted">Click a state to drill down</small>
            <div className="d-flex align-items-center gap-1">
              {colorScale.colors.map((color, i) => (
                <React.Fragment key={i}>
                  <div style={{ width: 12, height: 12, backgroundColor: color, borderRadius: 2, marginLeft: i > 0 ? 4 : 0 }} />
                  <small className="text-muted">{i === 0 ? 'Low' : i === colorScale.colors.length - 1 ? 'High' : 'Mid'}</small>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="card-body p-0">
        <GoogleMapDataLayer
          data={stateData}
          userType="all"
          colorScale={colorScale}
          onStateClick={handleStateClick}
          isLoading={isLoading}
          className="programmatic-map"
        />
      </div>
    </div>
  );
};

ProgrammaticMapView.displayName = 'ProgrammaticMapView';
export default ProgrammaticMapView;
